import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AUTH_SUCCESS_COPY, switchAccount } from '@/features/auth';
import { useAuth } from '@/providers/auth-provider';

import {
  changePassword,
  fetchProfile,
  resendChangePasswordOtp,
  resendPhoneOtp,
  sendChangePasswordOtp,
  sendCurrentPhoneOtp,
  sendNewPhoneOtp,
  updateProfile,
  verifyChangePasswordOtp,
  verifyPhoneOtp,
  type PhoneStage,
} from './api';
import { CHANGE_PASSWORD_COPY, EDIT_PROFILE_COPY, PHONE_CHANGE_COPY } from './constants';
import type { ProfilePanel } from './schemas';

export const profileKeys = {
  all: ['profile'] as const,
  detail: () => [...profileKeys.all, 'detail'] as const,
};

/**
 * The panel body, fetched when the panel mounts. No retry: AC "no retry" (117535 BR 23).
 *
 * A complete body keeps the app-default staleTime (grilling Q8), so a reopen inside it reuses the
 * cache. A partial body — or a complete one whose last refetch failed — is refetched on every open;
 * a failed call caches no data, and a data-less query always fetches on mount.
 */
export function useProfile() {
  return useQuery({
    queryKey: profileKeys.detail(),
    queryFn: fetchProfile,
    retry: false,
    refetchOnMount: (query) =>
      query.state.data?.partial || query.state.status === 'error' ? 'always' : true,
  });
}

/** Step 1. A failure keeps the Grower on Step 1; the toaster speaks the server's message. */
export function useSendChangePasswordOtp() {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_SENT },
    mutationFn: sendChangePasswordOtp,
  });
}

/** Step 2 resend. 429s and expiry are presented by the code-entry modal (`suppressErrorToast`). */
export function useResendChangePasswordOtp() {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_RESENT, suppressErrorToast: true },
    mutationFn: resendChangePasswordOtp,
  });
}

/** Step 2. `OTP_INVALID` renders inline, so the caller presents every failure (`suppressErrorToast`). */
export function useVerifyChangePasswordOtp() {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_VERIFIED, suppressErrorToast: true },
    mutationFn: verifyChangePasswordOtp,
  });
}

/** Step 3. The reuse error renders inline, so the caller presents every failure. */
export function useChangePassword() {
  return useMutation({
    meta: { successMessage: CHANGE_PASSWORD_COPY.SUCCESS, suppressErrorToast: true },
    mutationFn: changePassword,
  });
}

/**
 * Edit Profile's save (US-117543 §C). Success copy via `<MutationToaster>`; the error half is
 * suppressed because a 409 renders inline and the modal presents every other failure itself.
 *
 * The reply is the new identity, so it is merged into the session and the cached panel identity
 * rather than refetched: the header, avatar and panel show the new name at once.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user, setSession } = useAuth();
  return useMutation({
    mutationFn: updateProfile,
    meta: { successMessage: EDIT_PROFILE_COPY.SAVED, suppressErrorToast: true },
    onSuccess: (saved) => {
      if (user) setSession({ ...user, ...saved });
      queryClient.setQueryData<ProfilePanel>(profileKeys.detail(), (old) =>
        old ? { ...old, identity: { ...old.identity, ...saved } } : old,
      );
    },
  });
}

/**
 * ADR-0029 — Switch Profile. Mints a session for the grower's other linked account, adopts it the
 * same way a fresh login does, and drops every cached query: devices, fields, subscriptions,
 * notifications, addresses, payment methods, warranty, associate growers, the Profile body itself
 * — all of it is scoped to the *account*, not the browser session, and switching stays on the same
 * authenticated route tree that reads all of it (unlike `useLogout`, which lands on a public route
 * that reads none of it, so a narrower `removeQueries` sufficed there). Correction (2026-10-20):
 * this used to remove only the Profile body's own cache, which left the Map/Devices pages showing
 * the *previous* account's devices and fields until a manual reload. No mutation toast — same
 * reasoning as `useLogout` — the navigation the caller does on success is the feedback.
 */
export function useSwitchAccount() {
  const queryClient = useQueryClient();
  const { setSession } = useAuth();
  return useMutation({
    // Omitted from the Profile menu's one-other-account case; the Select Account screen passes
    // the chosen `accountType` explicitly, since there it is picking among more than one option.
    mutationFn: (accountType?: string) => switchAccount(accountType),
    meta: { suppressToast: true },
    onSuccess: (result) => {
      setSession(result.user);
      queryClient.clear();
    },
  });
}

/** Phone Step 1. A 503 is toasted by <MutationToaster> (the BE sends the AC copy); stays on Step 1. */
export function useSendCurrentPhoneOtp() {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_SENT },
    mutationFn: sendCurrentPhoneOtp,
  });
}

/** Phone Step 3. 409 / 400 / 422 render inline, so the step presents every failure. */
export function useSendNewPhoneOtp() {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_SENT, suppressErrorToast: true },
    mutationFn: sendNewPhoneOtp,
  });
}

/** Either code step's resend. 429s and expiry are presented by the code-entry modal. */
export function useResendPhoneOtp(stage: PhoneStage) {
  return useMutation({
    meta: { successMessage: AUTH_SUCCESS_COPY.CODE_RESENT, suppressErrorToast: true },
    mutationFn: (referenceId: string) => resendPhoneOtp(stage, referenceId),
  });
}

/** Either code step's verify. Verify-new's success copy lands on Login after the sign-out. */
export function useVerifyPhoneOtp(stage: PhoneStage) {
  return useMutation({
    meta: {
      successMessage: stage === 'new' ? PHONE_CHANGE_COPY.SUCCESS : AUTH_SUCCESS_COPY.CODE_VERIFIED,
      suppressErrorToast: true,
    },
    mutationFn: (body: { referenceId: string; otp: string }) => verifyPhoneOtp(stage, body),
  });
}
